import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Home, List, Map as MapIcon, Bell, LogOut, Monitor, UserRound } from 'lucide-react';
import { useAuthStore } from '@/store/auth-store';
import { useVehicles } from '@/hooks/use-vehicles';
import { useVehicleSync } from '@/hooks/use-vehicle-sync';
import { useAlarmHistory } from '@/hooks/use-alarm-history';
import { setUiMode } from '@/hooks/use-is-mobile';
import { UserPicker } from '@/components/ui/user-picker';
import { EventNotifier } from '@/components/notifications/event-notifier';
import { cn } from '@/lib/utils';
import { appBrand } from '@/lib/branding';

// =============================================================================
// MobileLayout — shell perangkat mobile:
//   ┌───────────────────────────────┐
//   │ Header: judul + switch user +  │  h-12
//   │         profil + desktop +log  │
//   ├───────────────────────────────┤
//   │          <Outlet />            │  flex-1
//   ├───────────────────────────────┤
//   │  Home | Devices | Map | Events │  bottom nav h-14
//   └───────────────────────────────┘
// Reuse infra global: useVehicleSync (WS→store), useAlarmHistory, EventNotifier.
// =============================================================================

export function MobileLayout() {
  // Initial REST load vehicle+positions (queryKey berisi effectiveUserId →
  // auto-refetch saat switch user) di shell, supaya data tersedia di SEMUA tab
  // (Home/Map/Events), bukan hanya saat buka tab Devices.
  useVehicles();
  useVehicleSync();
  useAlarmHistory(200);

  return (
    <div className="h-[100dvh] w-screen flex flex-col bg-background text-foreground overflow-hidden">
      <MobileHeader />
      <main className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </main>
      <MobileBottomNav />
      <EventNotifier />
    </div>
  );
}

const MTITLES: Array<[string, string]> = [
  ['/m/home', 'Beranda'],
  ['/m/devices', 'Devices'],
  ['/m/map', 'Peta'],
  ['/m/events', 'Events'],
  ['/m/profile', 'Profil'],
];
function mobileTitle(path: string): string {
  for (const [p, t] of MTITLES) if (path.startsWith(p)) return t;
  return appBrand.name;
}

function MobileHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const canSwitchUser = useAuthStore((s) => s.canSwitchUser());
  const viewAsUserId = useAuthStore((s) => s.viewAsUserId);
  const setViewAsUser = useAuthStore((s) => s.setViewAsUser);

  return (
    <header className="h-12 shrink-0 flex items-center gap-1.5 px-2 border-b border-border bg-chrome text-chrome-foreground">
      <span className="text-base font-semibold tracking-tight truncate shrink-0">
        {mobileTitle(location.pathname)}
      </span>

      {/* Switch user (admin/reseller) — combobox inline, mengisi ruang. */}
      {canSwitchUser && (
        <div className={cn('flex-1 min-w-0 rounded-md', viewAsUserId != null && 'ring-2 ring-amber-300')}>
          <UserPicker
            value={viewAsUserId ?? 0}
            onChange={(id) => { if (id === 0) setViewAsUser(null); }}
            onChangeMeta={(u) => { if (u) setViewAsUser(u.id, u.real_name || u.login); }}
            placeholder="Switch user…"
          />
        </div>
      )}
      {!canSwitchUser && <div className="flex-1" />}

      <button
        type="button"
        onClick={() => navigate('/m/profile')}
        title="Profil & password"
        aria-label="Profil"
        className="p-2 rounded-md hover:bg-accent text-muted-foreground shrink-0"
      >
        <UserRound className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => setUiMode('desktop')}
        title="Tampilan desktop"
        aria-label="Desktop"
        className="p-2 rounded-md hover:bg-accent text-muted-foreground shrink-0"
      >
        <Monitor className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={logout}
        title="Logout"
        aria-label="Logout"
        className="p-2 rounded-md hover:bg-accent text-muted-foreground shrink-0"
      >
        <LogOut className="w-4 h-4" />
      </button>
    </header>
  );
}

function MobileBottomNav() {
  const items = [
    { to: '/m/home', label: 'Home', icon: Home },
    { to: '/m/devices', label: 'Devices', icon: List },
    { to: '/m/map', label: 'Map', icon: MapIcon },
    { to: '/m/events', label: 'Events', icon: Bell },
  ];
  return (
    <nav className="h-14 shrink-0 border-t border-border bg-chrome text-chrome-foreground flex">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            <Icon className="w-5 h-5" />
            {it.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
